import { Injectable } from '@angular/core';
import {
    HttpEvent,
    HttpInterceptor,
    HttpHandler,
    HttpRequest,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Logger } from '../logger.service';
import { environment } from 'src/environments/environment';
import { rateLimitMessage } from './rate-limit';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to all requests.
 */
@Injectable({
    providedIn: 'root',
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
    constructor(private router: Router) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return next
            .handle(request)
            .pipe(catchError((error) => this.errorHandler(error, request)));
    }

    private errorHandler(
        error: HttpErrorResponse,
        request: HttpRequest<any>
    ): Observable<HttpEvent<any>> {
        if (!environment.production) {
            // G3: no volcar el objeto de error completo (puede incluir el request con
            // headers/token). Solo campos seguros.
            log.error('Request error', {
                status: error.status,
                url: error.url,
                message: error.message,
            });
        }

        if (error.status === 503) {
            log.warn('Sistema en mantenimiento detectado');

            const isMaintenanceStatusCheck = request.url.includes(
                '/maintenance/status'
            );
            // Ya en el login no se vuelve a navegar: con `onSameUrlNavigation: 'reload'`
            // el login se recarga, repite la petición que dio 503 y entra en ciclo.
            const isOnLogin = this.router.url.startsWith('/auth/login');

            if (!isMaintenanceStatusCheck && !isOnLogin) {
                this.router.navigate(['/auth/login'], {
               });

            }
        }

        // header de mantenimiento (header personalizado)
        if (error.headers?.get('X-Maintenance-Mode') === 'true') {
            log.warn('Modo mantenimiento detectado via header');
            this.router.navigate(['/auth/login'], {
/*                 queryParams: { maintenance: 'true' },
 */            });
        }

        // Otros errores (401, 403, etc)
        switch (error.status) {
            case 401:
                log.debug('Unauthorized');

                break;

            case 403:
                log.debug('Forbidden');
                // Mostrar mensaje de acceso denegado
                break;

            case 0:
                log.error('Network error - backend no disponible');
                // Backend completamente caído
                break;

            case 429:
                log.warn('Rate limit alcanzado', { url: error.url });
                // Sin reintento automático: un retry ante 429 alarga el bloqueo.
                return throwError(() => this.normalizeRateLimit(error));
        }

        return throwError(() => error);
    }

    /**
     * Deja el 429 con el envelope del backend (`error.error.message`) sin importar
     * quién lo respondió, para que cada pantalla lo muestre como cualquier otro error.
     */
    private normalizeRateLimit(error: HttpErrorResponse): HttpErrorResponse {
        return new HttpErrorResponse({
            error: {
                statusCode: 429,
                success: false,
                message: rateLimitMessage(error),
                data: null,
                errors: 'TooManyRequests',
            },
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
        });
    }
}
